import Seo from "@/components/Seo";

const Shell = ({ title, eyebrow, children, testid }) => (
  <main data-testid={testid} className="bg-paper pb-24 pt-32 sm:pt-40">
    <div className="container-x max-w-3xl">
      <p className="eyebrow text-ink/50">{eyebrow}</p>
      <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">{title}</h1>
      <div className="mt-10 space-y-8 text-base leading-relaxed text-ink/75">{children}</div>
    </div>
  </main>
);

const H = ({ children }) => <h2 className="font-display text-xl font-semibold text-ink">{children}</h2>;

export function PrivacyPolicy() {
  return (
    <>
      <Seo title="Privacy Policy | Electra HR" siteName="Electra HR" description="How Electra HR collects, uses and protects personal data. GDPR-compliant privacy notice for our website and clients." />
      <Shell title="Privacy Policy" eyebrow="Legal" testid="privacy-page">
        <p className="font-mono text-xs text-ink/45">Last updated: September 2026</p>
        <section>
          <H>Who we are</H>
          <p className="mt-2">
            Electra HR Ltd (company number 11077627), registered office: Suite 100, 7 Kenden Business Park,
            Maritime Close, Rochester, Kent, ME2 4JF. For the purposes of UK data protection law, Electra HR Ltd is the data
            controller. You can reach us at hello@electra-hr.com or 01622 957 300.
          </p>
        </section>
        <section>
          <H>What we collect</H>
          <p className="mt-2">We may collect and process:</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-6">
            <li>Name, email address, phone number and company name when you contact us by form, email or phone</li>
            <li>Information you give us about your HR situation or enquiry, which can include special category data (see below)</li>
            <li>Technical data such as IP address, browser type and pages visited, analytics only, and only where you have opted in via our cookie banner</li>
          </ul>
        </section>
        <section>
          <H>Special category data</H>
          <p className="mt-2">
            HR consultancy routinely involves special category data: health information in sickness and capability
            cases, and grievance or disciplinary material that can touch protected characteristics. When we process
            it, we rely on Article 9(2)(f) UK GDPR (establishment, exercise or defence of legal claims) and, where
            the processing supports your obligations as an employer, Article 9(2)(b) (employment and social security
            law), alongside an Article 6 lawful basis.
          </p>
        </section>
        <section>
          <H>How we use your information</H>
          <ul className="mt-3 list-disc space-y-1.5 pl-6">
            <li>To respond to your enquiry and provide HR support services</li>
            <li>To provide a quote or proposal for our services</li>
            <li>To administer and manage client relationships</li>
            <li>To comply with our legal and regulatory obligations</li>
            <li>To improve our website and services (consented analytics only)</li>
          </ul>
        </section>
        <section>
          <H>Lawful bases for processing</H>
          <p className="mt-2">We process personal data on the following Article 6 bases:</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-6">
            <li><strong>Legitimate interests</strong>: responding to enquiries, marketing our services, improving our website</li>
            <li><strong>Contract</strong>: where processing is necessary to perform a contract with you or take steps at your request before entering one</li>
            <li><strong>Legal obligation</strong>: where we must process data to meet legal or regulatory requirements</li>
            <li><strong>Consent</strong>: where you have given clear consent for a specific purpose, such as analytics cookies</li>
          </ul>
        </section>
        <section>
          <H>Escalation to Bonsai Law</H>
          <p className="mt-2">
            Where a matter escalates and you instruct Bonsai Law, Bonsai Law is a separate and independent data
            controller for the personal data it receives, not a processor acting for Electra HR, and not part of
            the same organisation or group. We only share personal data with Bonsai Law with your knowledge and on
            a lawful basis. From that point, Bonsai Law's own privacy policy applies to the legal work.
          </p>
        </section>
        <section>
          <H>Sharing your data</H>
          <p className="mt-2">We never sell personal data. We may share it with:</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-6">
            <li>Third-party service providers who help us operate (website hosting, email delivery), bound by contract to protect it</li>
            <li>Bonsai Law, on the basis described above</li>
            <li>Regulators or authorities where the law requires it</li>
          </ul>
        </section>
        <section>
          <H>How we store and protect your data</H>
          <p className="mt-2">
            We take appropriate technical and organisational measures to protect personal data against unauthorised
            access, loss or misuse, and we retain it only for as long as necessary for the purposes above, after
            which it is deleted or anonymised.
          </p>
        </section>
        <section>
          <H>Your rights</H>
          <p className="mt-2">Under UK GDPR you can ask to:</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-6">
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion (in certain circumstances)</li>
            <li>Object to or restrict our processing</li>
            <li>Receive your data in a structured, machine-readable format</li>
            <li>Withdraw consent at any time, where processing is based on consent</li>
          </ul>
          <p className="mt-3">To exercise any of these, email hello@electra-hr.com.</p>
        </section>
        <section>
          <H>Cookies</H>
          <p className="mt-2">
            Essential cookies keep the site working and remember your consent choice. Analytics cookies are off
            unless you switch them on. Full detail is in our{" "}
            <a href="/cookie-policy" data-testid="privacy-cookie-link" className="font-bold underline underline-offset-4 hover:text-ink/60">Cookie Policy</a>.
          </p>
        </section>
        <section>
          <H>Complaints</H>
          <p className="mt-2">
            If you're unhappy with how we've handled your personal data, you can complain to the Information
            Commissioner's Office at ico.org.uk or on 0303 123 1113.
          </p>
        </section>
        <section>
          <H>Changes to this policy</H>
          <p className="mt-2">We may update this policy from time to time. Changes will be posted on this page with an updated date.</p>
        </section>
      </Shell>
    </>
  );
}

export function CookiePolicy() {
  return (
    <>
      <Seo title="Cookie Policy | Electra HR" siteName="Electra HR" description="What cookies Electra HR uses, why, and how to control them. Analytics and marketing cookies are off unless you switch them on." />
      <Shell title="Cookie Policy" eyebrow="Legal" testid="cookies-page">
        <p>We keep cookies boring on purpose. Essential cookies run the site. Everything else is off until you switch it on, in line with PECR and UK GDPR.</p>
        <section>
          <H>Essential cookies</H>
          <p className="mt-2">These keep the site working and remember your consent choice. They don't track you and can't be switched off.</p>
        </section>
        <section>
          <H>Analytics & marketing cookies</H>
          <p className="mt-2">Off by default. If you accept them, they help us understand which pages are useful and whether our marketing works. Nothing non-essential fires before you opt in.</p>
        </section>
        <section>
          <H>Changing your mind</H>
          <p className="mt-2">
            You can reopen the consent panel and change your choices at any time:{" "}
            <button
              data-testid="cookies-reopen-consent-button"
              onClick={() => window.dispatchEvent(new Event("electra:open-consent"))}
              className="font-bold underline underline-offset-4 hover:text-ink/60"
            >
              manage cookie choices
            </button>
            .
          </p>
        </section>
      </Shell>
    </>
  );
}
